import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Cell
} from 'recharts';
import { AlertTriangle, CheckCircle, Clock, TrendingUp, Download, FileText, Loader2 } from 'lucide-react';
import { assessments } from '../services/api';

// ABLLS-R category names mapping
const categoryNames = {
  'A': 'Cooperação',
  'B': 'Desempenho Visual', 
  'C': 'Linguagem Receptiva',
  'D': 'Imitação Motora',
  'E': 'Imitação Vocal',
  'F': 'Solicitações',
  'G': 'Rotulação',
  'H': 'Intraverbal',
  'I': 'Vocalização Espontânea',
  'J': 'Gramática e Sintaxe',
  'K': 'Brincar e Lazer',
  'L': 'Interação Social',
  'M': 'Instrução em Grupo',
  'N': 'Rotinas de Sala de Aula',
  'P': 'Generalização',
  'Q': 'Leitura',
  'R': 'Matemática',
  'S': 'Escrita',
  'T': 'Ortografia',
  'U': 'Vestir-se',
  'V': 'Alimentação',
  'W': 'Higiene Pessoal',
  'X': 'Uso do Banheiro',
  'Y': 'Habilidades Motoras Grossas',
  'Z': 'Habilidades Motoras Finas',
};

// Color scheme
const COLORS = {
  excellent: '#48bb78',
  good: '#f6ad55',
  needs_improvement: '#fc8181',
  untested: '#cbd5e0'
};

const getPerformanceColor = (value) => {
  if (value >= 90) return COLORS.excellent;
  if (value >= 70) return COLORS.good;
  if (value > 0) return COLORS.needs_improvement;
  return COLORS.untested;
};

const ABLLSRDashboard = ({ assessmentData }) => {
  const dashboardData = assessmentData?.parsedData || null;
  const loading = false;
  const error = !dashboardData ? 'No assessment processed yet' : null;
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [parentReport, setParentReport] = useState(assessmentData?.latestAssessment?.parentReport || null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState(assessmentData?.latestAssessment?.parentReportGeneratedAt || null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;
    
    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const patientName = assessmentData?.latestAssessment?.patient?.firstName + ' ' + assessmentData?.latestAssessment?.patient?.lastName || 'Patient';
      const date = new Date().toISOString().split('T')[0];
      const filename = `ABLLS-R_Dashboard_${patientName.replace(/\s+/g, '_')}_${date}.pdf`;

      // Fix width so PDF looks the same regardless of screen/window size
      const el = dashboardRef.current;
      const originalWidth = el.style.width;
      el.style.width = '1024px';

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: el.scrollHeight,
        width: 1024
      });

      el.style.width = originalWidth;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const margin = 10;
      const ratio = (pdfWidth - 2 * margin) / imgWidth;
      const scaledWidth = pdfWidth - 2 * margin;
      const scaledHeight = imgHeight * ratio;

      const x = margin;
      const y = 10;

      if (scaledHeight > pdfHeight - 20) {
        let position = 0;
        const pageHeight = pdfHeight - 20;
        
        while (position < scaledHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          
          const remainingHeight = scaledHeight - position;
          const currentPageHeight = Math.min(pageHeight, remainingHeight);
          
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          
          tempCanvas.width = imgWidth;
          tempCanvas.height = (currentPageHeight / ratio);
          
          tempCtx.drawImage(canvas, 
            0, position / ratio, imgWidth, currentPageHeight / ratio,
            0, 0, imgWidth, currentPageHeight / ratio
          );
          
          const tempImgData = tempCanvas.toDataURL('image/png');
          pdf.addImage(tempImgData, 'PNG', x, y, scaledWidth, currentPageHeight);
          
          position += currentPageHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }
      
      pdf.setFontSize(16);
      pdf.setTextColor(40, 40, 40);
      pdf.text('ABLLS-R Assessment Dashboard', pdfWidth / 2, 8, { align: 'center' });

      // Add version footer to all pages
      const { getVersionString } = await import('../config/version');
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(getVersionString(), pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save(filename);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  // Generate parent report
  const generateParentReport = async () => {
    const assessmentId = assessmentData?.latestAssessment?.id;
    if (!assessmentId) return;

    setIsGeneratingReport(true);
    setReportError(null);
    try {
      const response = await assessments.generateParentReport(assessmentId);
      if (response.data.status === 'success') {
        setParentReport(response.data.data.report);
        setReportGeneratedAt(new Date().toISOString());
      } else {
        setReportError(response.data.message || 'Erro ao gerar relatório');
      }
    } catch (err) {
      console.error('Error generating parent report:', err);
      setReportError(err.response?.data?.message || 'Erro ao gerar relatório. Tente novamente.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Simple markdown renderer
  const renderMarkdown = (markdown) => {
    if (!markdown) return null;
    const lines = markdown.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-gray-800 mt-6 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        // Collect consecutive bullet items into a <ul>
        const items = [];
        while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
          items.push(
            <li key={i} className="text-gray-700 mb-1">
              {renderInlineMarkdown(lines[i].replace(/^[-*]\s/, ''))}
            </li>
          );
          i++;
          while (i < lines.length && lines[i].trim() === '') i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className="ml-4 list-disc mb-2">
            {items}
          </ul>
        );
        continue;
      } else if (/^\d+\.\s/.test(line)) {
        // Collect consecutive numbered items (with description paragraphs) into an <ol>
        const items = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
          const titleLine = lines[i].replace(/^\d+\.\s/, '');
          i++;
          while (i < lines.length && lines[i].trim() === '') i++;
          const descParts = [];
          while (i < lines.length && !/^\d+\.\s/.test(lines[i]) && !lines[i].startsWith('#') && !lines[i].startsWith('---')) {
            if (lines[i].trim() === '') { i++; continue; }
            descParts.push(
              <p key={`desc-${i}`} className="text-gray-700 mt-1 leading-relaxed">
                {renderInlineMarkdown(lines[i])}
              </p>
            );
            i++;
          }
          items.push(
            <li key={`li-${items.length}`} className="text-gray-700 mb-3">
              {renderInlineMarkdown(titleLine)}
              {descParts}
            </li>
          );
        }
        elements.push(
          <ol key={`ol-${i}`} className="ml-4 list-decimal mb-2">
            {items}
          </ol>
        );
        continue;
      } else if (line.startsWith('---')) {
        elements.push(<hr key={i} className="my-4 border-gray-200" />);
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(
          <p key={i} className="text-gray-700 mb-2 leading-relaxed">
            {renderInlineMarkdown(line)}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  const renderInlineMarkdown = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error || !dashboardData?.ABLLS_R) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <div className="text-yellow-800 font-medium mb-2">No Assessment Processed Yet</div>
        <div className="text-yellow-700 text-sm">
          The assessment needs to be analyzed before the dashboard can be displayed.
        </div>
      </div>
    );
  }

  const ablls = dashboardData.ABLLS_R;
  const progress = ablls.assessment_progress;
  const scores = ablls.performance_scores;

  // Process data for charts
  const assessedDomains = [];
  const untestedDomains = [];

  Object.entries(scores).forEach(([key, data]) => {
    const letter = key.split('_')[0];
    const name = categoryNames[letter] || key;
    
    if (data.items_tested > 0) {
      assessedDomains.push({
        letter,
        name,
        shortName: name.length > 15 ? name.substring(0, 12) + '...' : name,
        label: `${letter}: ${name}`,
        masteryRate: data.mastery_rate,
        performanceScore: data.percentage_of_max,
        itemsTested: data.items_tested,
        itemsMastered: data.items_mastered,
        actualScore: data.actual_score,
        maxScore: data.max_score
      });
    } else {
      untestedDomains.push({
        letter,
        name,
        label: `${letter}: ${name}`
      });
    }
  });

  assessedDomains.sort((a, b) => a.letter.localeCompare(b.letter));
  untestedDomains.sort((a, b) => a.letter.localeCompare(b.letter));

  const totalTested = assessedDomains.reduce((sum, d) => sum + d.itemsTested, 0);
  const totalMastered = assessedDomains.reduce((sum, d) => sum + d.itemsMastered, 0);
  const overallMasteryRate = totalTested > 0 ? Math.round((totalMastered / totalTested) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={exportToPDF}
          disabled={isExporting}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
            isExporting 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
          }`}
        >
          {isExporting ? (
            <>
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Exportando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </>
          )}
        </button>
      </div>

      {/* Dashboard Content */}
      <div ref={dashboardRef} className="bg-white">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Dashboard ABLLS-R</h2>
              <p className="text-sm text-gray-600 mt-1">
                Relatório Abrangente de Avaliação de Habilidades
              </p>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p>Data: {new Date().toLocaleDateString('pt-BR')}</p>
              {assessmentData?.latestAssessment?.patient && (
                <p>
                  Paciente: {assessmentData.latestAssessment.patient.firstName} {assessmentData.latestAssessment.patient.lastName}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-brand-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Progresso da Avaliação</div>
                  <div className="text-2xl font-bold text-gray-900">{progress.percent_assessed}%</div>
                  <div className="text-xs text-gray-500">{progress.items_tested} de {progress.total_items} itens</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Itens Dominados</div>
                  <div className="text-2xl font-bold text-gray-900">{totalMastered}</div>
                  <div className="text-xs text-gray-500">de {totalTested} testados</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-brand-600 to-brand-500 mr-3 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">%</span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Taxa de Domínio Geral</div>
                  <div className="text-2xl font-bold text-gray-900">{overallMasteryRate}%</div>
                  <div className="text-xs text-gray-500">Apenas itens testados</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Domínios Avaliados</div>
                  <div className="text-2xl font-bold text-gray-900">{assessedDomains.length}</div>
                  <div className="text-xs text-gray-500">de 25 domínios totais</div>
                </div>
              </div>
            </div>
          </div>

          {/* How to read this report */}
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 text-sm text-gray-700">
            <p className="font-medium text-gray-800 mb-2">Como ler este relatório</p>
            <p className="mb-1">
              O ABLLS-R avalia <strong>25 áreas</strong> do desenvolvimento organizadas em 4 grupos: <strong>Habilidades Básicas</strong> (cooperação, linguagem, imitação, brincar, socialização), <strong>Acadêmicas</strong> (leitura, matemática, escrita), <strong>Autocuidado</strong> (vestir-se, alimentação, higiene) e <strong>Motoras</strong> (motricidade grossa e fina).
            </p>
            <p className="mb-1">
              Cada item é pontuado de <strong>0 a 2</strong>: <strong>0</strong> = não demonstra, <strong>1</strong> = emergindo (faz parcialmente ou com ajuda), <strong>2</strong> = dominado. A taxa de domínio mostra a porcentagem de itens com pontuação máxima.
            </p>
            <p>
              Nos gráficos: <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#48bb78'}}></span> <strong>Verde</strong> (≥90%) = excelente &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#f6ad55'}}></span> <strong>Laranja</strong> (70–89%) = bom &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#fc8181'}}></span> <strong>Vermelho</strong> (&lt;70%) = precisa melhorar
            </p>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mastery Rates Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Taxas de Domínio por Área</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={assessedDomains} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="label" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value}%`,
                        'Taxa de Domínio'
                      ]}
                      labelFormatter={(label) => label}
                      contentStyle={{ backgroundColor: '#f7fafc', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="masteryRate" name="Taxa de Domínio (%)">
                      {assessedDomains.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getPerformanceColor(entry.masteryRate)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Performance Radar Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pontuações de Desempenho</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={assessedDomains}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="shortName" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis 
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Radar
                      name="Desempenho"
                      dataKey="performanceScore"
                      stroke="#667eea"
                      fill="#667eea"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                    <Tooltip formatter={(value) => [`${value}%`, 'Desempenho']} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Detailed Performance Chart */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Desempenho Detalhado por Domínio</h3>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={assessedDomains} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="label" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label) => label}
                    contentStyle={{ backgroundColor: '#f7fafc', border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="itemsMastered" stackId="a" fill={COLORS.excellent} name="Itens Dominados" />
                  <Bar 
                    dataKey={(data) => data.itemsTested - data.itemsMastered} 
                    stackId="a" 
                    fill={COLORS.needs_improvement} 
                    name="Itens Não Dominados" 
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Domain Status Grid */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Status de Todos os Domínios ABLLS-R</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {/* Assessed Domains */}
              {assessedDomains.map((domain) => (
                <div key={domain.letter} className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-2"
                    style={{ backgroundColor: getPerformanceColor(domain.performanceScore) }}
                  >
                    {domain.letter}
                  </div>
                  <div className="text-xs text-gray-600 mb-1 leading-tight">{domain.name}</div>
                  <div className="text-sm font-medium text-gray-900">{domain.performanceScore}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${domain.performanceScore}%`,
                        backgroundColor: getPerformanceColor(domain.performanceScore)
                      }}
                    ></div>
                  </div>
                </div>
              ))}
              
              {/* Untested Domains */}
              {untestedDomains.map((domain) => (
                <div key={domain.letter} className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-gray-600 text-xl font-bold mx-auto mb-2"
                    style={{ backgroundColor: COLORS.untested }}
                  >
                    {domain.letter}
                  </div>
                  <div className="text-xs text-gray-600 mb-1 leading-tight">{domain.name}</div>
                  <div className="text-xs text-gray-400">Não Testado</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div className="h-2 rounded-full bg-gray-300" style={{ width: '0%' }}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.excellent }}></div>
                <span>Excelente (90%+)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.good }}></div>
                <span>Bom (70-89%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.needs_improvement }}></div>
                <span>Precisa Melhorar (&lt;70%)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS.untested }}></div>
                <span>Não Testado</span>
              </div>
            </div>
          </div>

          {/* Parent Report Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Relatório para Família</h3>
                <p className="text-sm text-gray-500">
                  Relatório em linguagem acessível para compartilhar com os pais
                  {reportGeneratedAt && (
                    <span className="ml-2 text-gray-400">
                      — Gerado em {new Date(reportGeneratedAt).toLocaleDateString('pt-BR')} às {new Date(reportGeneratedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={generateParentReport}
                disabled={isGeneratingReport}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isGeneratingReport
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
                }`}
              >
                {isGeneratingReport ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {parentReport ? 'Gerar Novamente' : 'Gerar Relatório'}
                  </>
                )}
              </button>
            </div>

            {reportError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{reportError}</p>
              </div>
            )}

            {parentReport && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 prose prose-sm max-w-none">
                {renderMarkdown(parentReport)}
              </div>
            )}

            {!parentReport && !reportError && !isGeneratingReport && (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Clique em "Gerar Relatório" para criar um relatório em linguagem acessível para os pais.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ABLLSRDashboard;